import { withAlpha } from "@/lib/color";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * TeamLedgerPanel — schema di tutti i movimenti finanziari della squadra.
 * Mostra entrate (verde) e uscite (rosso) verso OP Cassa, altre squadre (mercato/obblighi).
 *
 * Collassabile, posizionato dopo il pannello "Scheda finanziaria" sulla pagina squadra.
 */
export default function TeamLedgerPanel({ teamId, reloadKey = 0, isAdmin = false }) {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState("all"); // 'all' | 'in' | 'out'
    const [audit, setAudit] = useState(null);
    const [auditLoading, setAuditLoading] = useState(false);
    const [localReload, setLocalReload] = useState(0);
    const [editStart, setEditStart] = useState(false);
    const [startInput, setStartInput] = useState("");
    const [savingStart, setSavingStart] = useState(false);

    const saveOfficialStart = async () => {
        const mln = parseFloat(String(startInput).replace(",", "."));
        if (isNaN(mln)) return;
        setSavingStart(true);
        try {
            await api.put(`/admin/teams/${teamId}/initial-budget`, { initial_budget: Math.round(mln * 1_000_000) });
            setEditStart(false);
            setLocalReload((k) => k + 1);
        } catch { /* ignore */ } finally { setSavingStart(false); }
    };

    const runAudit = async () => {
        setAuditLoading(true);
        try {
            const r = await api.get(`/admin/teams/${teamId}/budget-audit`);
            setAudit(r.data);
        } catch (e) {
            setAudit({ error: e?.response?.data?.detail || "Errore audit" });
        } finally {
            setAuditLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        api.get(`/teams/${teamId}/ledger`).then((r) => {
            if (!cancelled) setData(r.data);
        }).catch(() => {/* ignore */});
        return () => { cancelled = true; };
    }, [teamId, reloadKey, localReload]);

    if (!data) return null;
    const { total_in: totalIn, total_out: totalOut, net, entries = [], by_kind: byKind = {},
        total_entries: totalEntries, current_budget: currentBudget, implied_starting_budget: impliedStart,
        official_starting_budget: officialStart, starting_budget_diff: startDiff } = data;
    const filtered = filter === "all" ? entries : entries.filter((e) => e.direction === filter);
    const hasMovements = entries.length > 0;
    const opCount = totalEntries != null ? totalEntries : entries.length;

    return (
        <div className="op-card mb-10" data-testid="team-ledger-panel">
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 p-5 hover:bg-t1a1d24 transition-colors cursor-pointer"
                data-testid="team-ledger-toggle"
                aria-expanded={open}
            >
                <div className="flex items-center gap-3">
                    <div className="op-label text-te8ff00">// MOVIMENTI SQUADRA</div>
                    {hasMovements && (
                        <span className="op-mono text-[10px] text-t8f95a3">
                            {opCount} operazioni
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-4">
                    <div className="op-mono text-xs text-t2bb6ff hidden sm:block" data-testid="ledger-total-in">
                        ENTRATE +{formatMln(totalIn)}
                    </div>
                    <div className="op-mono text-xs text-tff3b30 hidden sm:block" data-testid="ledger-total-out">
                        USCITE −{formatMln(totalOut)}
                    </div>
                    <div className={`op-mono text-sm font-bold ${net >= 0 ? "text-t2bb6ff" : "text-tff3b30"}`} data-testid="ledger-net">
                        NET {net >= 0 ? "+" : "−"}{formatMln(Math.abs(net))}
                    </div>
                    <span
                        className="flex items-center gap-1.5 op-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border rounded-sm shrink-0 whitespace-nowrap"
                        style={{ borderColor: "rgb(var(--tc-e8ff00))", color: "rgb(var(--tc-e8ff00))", background: "rgba(232,255,0,0.10)" }}
                        data-testid="team-ledger-toggle-cta"
                    >
                        {open ? "Chiudi" : "Apri dettaglio"}
                        <span
                            className="inline-block text-xs"
                            style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}
                        >
                            ▸
                        </span>
                    </span>
                </div>
            </button>

            {open && (
                <div className="border-t border-t22252a p-5">
                    {!hasMovements && (
                        <div className="op-mono text-xs text-t8f95a3 text-center py-6" data-testid="ledger-empty">
                            Nessun movimento registrato.
                        </div>
                    )}

                    {hasMovements && (
                        <>
                            {/* Riconciliazione budget (solo admin) */}
                            {isAdmin && (
                            <div className="mb-5 border border-t22252a bg-t13161b p-3" data-testid="ledger-reconciliation">
                                <div className="op-label text-[10px] text-t8f95a3 mb-2">// RICONCILIAZIONE BUDGET</div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <ReconCell label="Budget iniziale (implicito)" value={impliedStart} color="rgb(var(--tc-e8ff00))" testId="recon-start" />
                                    <ReconCell label="Netto movimenti" value={net} signed color={net >= 0 ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-ff3b30))"} testId="recon-net" />
                                    <ReconCell label="Budget attuale" value={currentBudget} color="rgb(var(--tc-ffffff))" testId="recon-current" />
                                    <div className="flex flex-col justify-center">
                                        <div className="op-mono text-[9px] text-t5c6370 uppercase">Verifica</div>
                                        <div className="op-mono text-xs text-t8f95a3">
                                            {formatMln(impliedStart)} {net >= 0 ? "+" : "−"} {formatMln(Math.abs(net))} = {formatMln(currentBudget)}
                                        </div>
                                    </div>
                                </div>

                                {/* Budget iniziale UFFICIALE + confronto */}
                                <div className="mt-3 pt-3 border-t border-t22252a flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="official-start-row">
                                    <div className="flex flex-col">
                                        <div className="op-mono text-[9px] text-t5c6370 uppercase">Budget iniziale ufficiale</div>
                                        {officialStart != null ? (
                                            <div className="op-mono text-sm font-bold text-t2bb6ff" data-testid="official-start-value">{formatMln(officialStart)}</div>
                                        ) : (
                                            <div className="op-mono text-xs text-t5c6370" data-testid="official-start-empty">non impostato</div>
                                        )}
                                    </div>
                                    {officialStart != null && (
                                        <div className="flex flex-col" data-testid="start-diff">
                                            <div className="op-mono text-[9px] text-t5c6370 uppercase">Confronto</div>
                                            {startDiff === 0 ? (
                                                <div className="op-mono text-sm font-bold text-t2bd96b">✓ riconcilia</div>
                                            ) : (
                                                <div className="op-mono text-sm font-bold text-tffb020">
                                                    ⚠ scarto {startDiff > 0 ? "+" : "−"}{formatMln(Math.abs(startDiff))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {isAdmin && !editStart && (
                                        <button
                                            onClick={() => { setEditStart(true); setStartInput(officialStart != null ? (officialStart / 1_000_000).toString() : (impliedStart / 1_000_000).toFixed(2)); }}
                                            data-testid="edit-official-start-btn"
                                            className="op-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border ml-auto"
                                            style={{ borderColor: "rgb(var(--tc-2bb6ff))", color: "rgb(var(--tc-2bb6ff))", background: "rgba(43,182,255,0.08)" }}
                                        >
                                            {officialStart != null ? "Modifica" : "Imposta budget iniziale"}
                                        </button>
                                    )}
                                    {isAdmin && editStart && (
                                        <div className="flex items-center gap-2 ml-auto">
                                            <input
                                                type="number"
                                                value={startInput}
                                                onChange={(e) => setStartInput(e.target.value)}
                                                placeholder="es. 786.95"
                                                data-testid="official-start-input"
                                                className="op-mono text-xs bg-t0e1116 border border-t22252a px-2 py-1.5 w-28 text-tffffff"
                                            />
                                            <span className="op-mono text-[10px] text-t5c6370">mln</span>
                                            <button onClick={saveOfficialStart} disabled={savingStart} data-testid="save-official-start-btn"
                                                className="op-mono text-[10px] uppercase px-3 py-1.5 border" style={{ borderColor: "rgb(var(--tc-2bd96b))", color: "rgb(var(--tc-2bd96b))" }}>
                                                {savingStart ? "..." : "Salva"}
                                            </button>
                                            <button onClick={() => setEditStart(false)} data-testid="cancel-official-start-btn"
                                                className="op-mono text-[10px] uppercase px-3 py-1.5 border" style={{ borderColor: "rgb(var(--tc-5c6370))", color: "rgb(var(--tc-8f95a3))" }}>
                                                Annulla
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="op-mono text-[10px] text-t5c6370 mt-2">
                                    {totalEntries} movimenti totali nello storico · netto calcolato sull'intera storia.
                                </div>
                            </div>
                            )}

                            {/* Audit forense (solo admin) */}
                            {isAdmin && (
                                <div className="mb-5 border border-ta78bfa/33 bg-t15131f p-3" data-testid="ledger-audit-block">
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                        <div className="op-label text-[10px] text-ta78bfa">// AUDIT FORENSE BUDGET (ADMIN)</div>
                                        <button
                                            onClick={runAudit}
                                            disabled={auditLoading}
                                            data-testid="run-budget-audit-btn"
                                            className="op-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border"
                                            style={{ borderColor: "rgb(var(--tc-a78bfa))", color: "rgb(var(--tc-a78bfa))", background: "rgba(167,139,250,0.10)" }}
                                        >
                                            {auditLoading ? "Analisi..." : "▸ Esegui audit"}
                                        </button>
                                    </div>
                                    {audit && audit.error && (
                                        <div className="op-mono text-xs text-tff3b30">{audit.error}</div>
                                    )}
                                    {audit && !audit.error && (
                                        <div data-testid="budget-audit-result">
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                                                <AuditCell label="Tesoreria" v={audit.sources.treasury.net} />
                                                <AuditCell label="Obblighi" v={audit.sources.obblighi.net} />
                                                <AuditCell label="Mercato" v={audit.sources.market.net} />
                                                <AuditCell label="Premi" v={audit.sources.prizes.net} />
                                                <AuditCell label="Estero" v={audit.sources.bots.net} />
                                                <AuditCell label="Riscatti prestiti" v={audit.sources.loan_settlements.net} />
                                            </div>
                                            <div className="op-mono text-[11px] text-tc9cdd6 border-t border-t22252a pt-2">
                                                Netto ledger: <b style={{ color: audit.ledger_net >= 0 ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-ff3b30))" }}>{audit.ledger_net >= 0 ? "+" : "−"}{formatMln(Math.abs(audit.ledger_net))}</b>
                                                {"  ·  "}Budget attuale: <b className="text-tffffff">{formatMln(audit.current_budget)}</b>
                                                {"  ·  "}Budget iniziale implicito: <b style={{ color: "rgb(var(--tc-e8ff00))" }}>{formatMln(audit.implied_starting_budget)}</b>
                                            </div>
                                            <div className="op-mono text-[10px] text-t8f95a3 mt-2">
                                                Tassa 10% registrata: <b style={{ color: audit.has_annual_tax ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-ff3b30))" }}>{audit.has_annual_tax ? "SÌ" : "NO"}</b>
                                                {"  ·  "}Stipendi registrati: <b style={{ color: audit.has_stipend ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-ff3b30))" }}>{audit.has_stipend ? "SÌ" : "NO"}</b>
                                                {"  ·  "}Movimenti tesoreria (stringa/ObjectId): {audit.treasury_movements_as_string}/{audit.treasury_movements_as_objectid}
                                            </div>
                                            {(audit.notes || []).map((n, i) => (
                                                <div key={i} className="op-mono text-[10px] text-tffb020 mt-1">{n}</div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Aggregated by kind */}
                            <div className="mb-5">
                                <div className="op-label text-[10px] text-t8f95a3 mb-2">// AGGREGATO PER TIPO</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {Object.entries(byKind)
                                        .sort(([, a], [, b]) => (b.in + b.out) - (a.in + a.out))
                                        .map(([key, v]) => (
                                            <div key={key} className="border border-t22252a p-2.5">
                                                <div className="op-mono text-[10px] text-t8f95a3 uppercase truncate" title={v.label}>
                                                    {v.label} <span className="text-t5c6370">×{v.count}</span>
                                                </div>
                                                <div className="flex justify-between mt-1">
                                                    {v.in > 0 && (
                                                        <span className="op-mono text-xs text-t2bb6ff">+{formatMln(v.in)}</span>
                                                    )}
                                                    {v.out > 0 && (
                                                        <span className="op-mono text-xs text-tff3b30">−{formatMln(v.out)}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>

                            {/* Filter bar */}
                            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-t22252a">
                                <span className="op-label text-[10px] text-t8f95a3">FILTRA:</span>
                                <FilterBtn label="Tutti" active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all" />
                                <FilterBtn label="Entrate" active={filter === "in"} onClick={() => setFilter("in")} color="rgb(var(--tc-2bb6ff))" testId="filter-in" />
                                <FilterBtn label="Uscite" active={filter === "out"} onClick={() => setFilter("out")} color="rgb(var(--tc-ff3b30))" testId="filter-out" />
                                <span className="op-mono text-[10px] text-t5c6370 ml-auto">{filtered.length} righe</span>
                            </div>

                            {/* Detail list */}
                            <div className="space-y-1 max-h-[480px] overflow-y-auto" data-testid="ledger-list">
                                {filtered.map((e, i) => (
                                    <LedgerRow key={`${e.id}-${i}`} entry={e} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

        </div>
    );
}


function ReconCell({ label, value, color = "rgb(var(--tc-ffffff))", signed = false, testId }) {
    const sign = signed ? (value >= 0 ? "+" : "−") : "";
    const display = signed ? formatMln(Math.abs(value)) : formatMln(value);
    return (
        <div className="flex flex-col justify-center" data-testid={testId}>
            <div className="op-mono text-[9px] text-t5c6370 uppercase">{label}</div>
            <div className="op-mono text-sm font-bold" style={{ color }}>
                {sign}{display}
            </div>
        </div>
    );
}


function AuditCell({ label, v }) {
    const color = v > 0 ? "rgb(var(--tc-2bb6ff))" : v < 0 ? "rgb(var(--tc-ff3b30))" : "rgb(var(--tc-8f95a3))";
    const sign = v > 0 ? "+" : v < 0 ? "−" : "";
    return (
        <div className="border border-t22252a p-2">
            <div className="op-mono text-[9px] text-t8f95a3 uppercase">{label}</div>
            <div className="op-mono text-xs font-bold" style={{ color }}>{sign}{formatMln(Math.abs(v))}</div>
        </div>
    );
}


function FilterBtn({ label, active, onClick, color = "rgb(var(--tc-ffffff))", testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className="op-mono text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors"
            style={{
                borderColor: active ? color : "rgb(var(--tc-22252a))",
                color: active ? color : "rgb(var(--tc-8f95a3))",
                background: active ? withAlpha(color, "11") : "transparent",
            }}
        >
            {label}
        </button>
    );
}


function LedgerRow({ entry }) {
    const isIn = entry.direction === "in";
    const sign = isIn ? "+" : "−";
    const color = isIn ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-ff3b30))";
    const sourceBadge = {
        treasury: { label: "OP", color: "rgb(var(--tc-e8ff00))" },
        obblighi: { label: "PRESTITO", color: "rgb(var(--tc-ff6b00))" },
        market: { label: "MERCATO", color: "rgb(var(--tc-2bb6ff))" },
        bots: { label: "ESTERO", color: "rgb(var(--tc-ff3b30))" },
        loans: { label: "RISCATTO", color: "rgb(var(--tc-a78bfa))" },
    }[entry.source] || { label: entry.source, color: "rgb(var(--tc-8f95a3))" };

    return (
        <div className="flex items-center gap-3 py-2 px-2.5 border-l-2 hover:bg-t1a1d24 transition-colors" style={{ borderLeftColor: withAlpha(color, "44") }} data-testid={`ledger-row-${entry.id}`}>
            <div className="op-mono text-[10px] text-t5c6370 w-20 shrink-0">
                {entry.date ? entry.date.slice(0, 10) : "—"}
            </div>
            <div
                className="op-mono text-[8px] uppercase px-1.5 py-0.5 border w-20 text-center shrink-0"
                style={{ color: sourceBadge.color, borderColor: withAlpha(sourceBadge.color, "55") }}
            >
                {sourceBadge.label}
            </div>
            <div className="flex-1 min-w-0">
                <div className="op-mono text-xs text-tffffff truncate">
                    {entry.label}
                    {entry.description && (
                        <span className="text-t8f95a3 ml-2">· {entry.description}</span>
                    )}
                </div>
                <div className="op-mono text-[10px] text-t5c6370">
                    vs {entry.counterparty}
                </div>
            </div>
            <div className="op-mono text-sm font-bold shrink-0" style={{ color }}>
                {entry.player_move
                    ? (isIn ? "↓ entra" : "↑ esce")
                    : `${sign}${formatMln(entry.amount)}`}
            </div>
        </div>
    );
}


function formatMln(v) {
    if (v == null) return "—";
    if (v === 0) return "0";
    const n = v / 1_000_000;
    if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(2)}mld`;
    return `${n.toFixed(2)}mln`;
}
